import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import toast from 'react-hot-toast';

// Modal para escribir el número de guía de despacho.
// Recibe TODOS los tóners de un mismo cambio (`registros`): el número se
// guarda una sola vez y queda en todos. `registro` se mantiene por
// compatibilidad con las pantallas que envían uno solo.
export default function GuiaModal({
  isOpen,
  onClose,
  registros,
  registro,
  entrega,
  personalEmpresa = [],
  onGuiaActualizada,
}) {
  const [guia, setGuia] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const lista = (registros && registros.length ? registros : (registro ? [registro] : []));
  const varios = lista.length > 1;
  const primero = lista[0] || null;

  // Estos dos datos son opcionales al aprobar, así que acá se pueden
  // completar o corregir junto con la guía.
  const [quienEntrega, setQuienEntrega] = useState('');
  const [fechaDespacho, setFechaDespacho] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setQuienEntrega(primero?.entregado_por || '');
    setFechaDespacho(primero?.fecha_despacho || '');
  }, [isOpen, primero?.entregado_por, primero?.fecha_despacho]);

  const faltaAlgo = !primero?.entregado_por || !primero?.fecha_despacho;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!guia.trim()) {
      toast.error('La guía es requerida');
      return;
    }

    if (!primero) {
      toast.error('No hay registros para actualizar');
      return;
    }

    setIsLoading(true);

    try {
      const ids = lista.map((r) => r.id).filter((id) => id !== null && id !== undefined);

      // Se actualizan los registros por su id (uno o todos los del cambio).
      // Si por alguna razón no llegan los ids, se usan los datos clave.
      const cambios = { guia: guia.trim() };
      // Solo se mandan si tienen valor: así nunca borran lo que ya estaba
      if (quienEntrega) cambios.entregado_por = quienEntrega;
      if (fechaDespacho) cambios.fecha_despacho = fechaDespacho;

      let consulta = supabase.from('registros').update(cambios);
      if (ids.length === lista.length) {
        consulta = consulta.in('id', ids);
      } else {
        consulta = consulta
          .eq('serie_maquina', primero.serie_maquina)
          .eq('fecha', primero.fecha)
          .eq('cliente_id', primero.cliente_id)
          .eq('color', primero.color);
      }

      const { data, error: registrosError } = await consulta.select();

      if (registrosError) {
        console.error('Error de Supabase:', registrosError);
        throw registrosError;
      }

      if (!data || data.length === 0) {
        toast.error('No se encontró el registro en la tabla de registros. Puede que ya haya sido limpiado.');
        onClose();
        return;
      }

      toast.success(varios ? `Guía agregada a los ${lista.length} tóners` : 'Guía agregada correctamente');
      // onGuiaActualizada recibe también lo que se completó, para refrescar la vista
      onGuiaActualizada(ids, guia.trim(), lista, {
        entregado_por: quienEntrega || null,
        fecha_despacho: fechaDespacho || null,
      });
      setGuia('');
      onClose();
    } catch (error) {
      console.error('Error al actualizar guía:', error);
      toast.error('Error al agregar la guía: ' + (error.message || 'Error desconocido'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setGuia('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-5 sm:p-6 rounded-lg shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold mb-2 text-gray-800">Agregar Guía de Despacho</h2>

        {primero && (
          <p className="text-sm text-gray-600 mb-4">
            {primero.clientes?.nombre ? `${primero.clientes.nombre} · ` : ''}
            <span className="font-mono">{primero.serie_maquina}</span>
            {varios && (
              <span className="block mt-1 font-medium text-gray-700">
                La guía queda en los {lista.length} tóners: {lista.map((r) => r.color).join(', ')}
              </span>
            )}
            {entrega && (
              <span className="block mt-1">
                Entrega: <span className="font-semibold text-gray-800">{entrega}</span>
              </span>
            )}
          </p>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Número de Guía
            </label>
            <input
              type="text"
              value={guia}
              onChange={(e) => setGuia(e.target.value)}
              className="w-full border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ingrese el número de guía"
              required
              disabled={isLoading}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Persona encargada de la entrega
              <span className="font-normal text-gray-500"> (opcional)</span>
            </label>
            <select
              value={quienEntrega}
              onChange={(e) => setQuienEntrega(e.target.value)}
              className="w-full border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              disabled={isLoading}
            >
              <option value="">-- Sin definir --</option>
              {personalEmpresa.map((persona) => (
                <option key={persona.id} value={persona.id}>{persona.nombre}</option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Será despachado
              <span className="font-normal text-gray-500"> (opcional)</span>
            </label>
            <input
              type="date"
              value={fechaDespacho}
              onChange={(e) => setFechaDespacho(e.target.value)}
              className="w-full border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isLoading}
            />
          </div>

          {faltaAlgo && (
            <p className="text-xs text-gray-500 mb-4 -mt-1">
              Estos datos quedaron pendientes al aprobar. Puedes completarlos ahora o dejarlos para después.
            </p>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400"
              disabled={isLoading}
            >
              {isLoading ? 'Guardando...' : 'Guardar Guía'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
